import { useEffect, useState } from "react";
import { Camera, PackageX, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../../lib/api";
import { compressImage } from "../../lib/compressImage";
import { useAuth } from "../../context/AuthContext";
import { ZoomImg } from "../ZoomImg";

const API_BASE = api.defaults.baseURL;
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export function ReturnDialog({ open, onOpenChange, stock, onDone }) {
  const [form, setForm] = useState({ item_name: "", qty: 1, reason: "" });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const avail = stock.filter((s) => s.remaining > 0);
  const sel = stock.find((s) => s.name === form.item_name);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, qty: Number(form.qty) || 1 }).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append("photo", await compressImage(photo), "rusak.jpg");
      await api.post("/asset-returns", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Retur ${form.item_name} × ${form.qty} diajukan — stok dikurangi`);
      setForm({ item_name: "", qty: 1, reason: "" }); setPhoto(null); onOpenChange(false); onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dialog-asset-return">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Undo2 className="w-5 h-5 text-rose-500" /> Retur Alat Rusak</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Nama Alat</Label>
            <Select value={form.item_name} onValueChange={(v) => setForm({ ...form, item_name: v })}>
              <SelectTrigger data-testid="return-item"><SelectValue placeholder="Pilih alat dari stok..." /></SelectTrigger>
              <SelectContent>{avail.map((s) => <SelectItem key={s.name} value={s.name} data-testid={`return-item-opt-${s.name.replace(/\W+/g, "-")}`}>{s.name} (sisa {s.remaining})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Jumlah</Label><Input type="number" min="1" max={sel?.remaining || 1} required value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="return-qty" /></div>
          <div className="space-y-1.5"><Label>Alasan / Kondisi Kerusakan</Label><Textarea rows={3} required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="cth: ONT mati total, port LAN rusak" data-testid="return-reason" /></div>
          <div className="space-y-1.5"><Label>Foto Alat Rusak <span className="text-muted-foreground font-normal">(opsional, untuk dicek admin)</span></Label>
            {photo ? (
              <div className="relative inline-block"><img src={URL.createObjectURL(photo)} alt="alat rusak" className="h-24 rounded-lg border border-border object-cover" data-testid="return-photo-preview" />
                <button type="button" onClick={() => setPhoto(null)} className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-0.5" data-testid="return-photo-remove"><X className="w-3 h-3" /></button></div>
            ) : (
              <label className="flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2.5 text-xs text-muted-foreground cursor-pointer hover:border-rose-500/50" data-testid="return-photo-label">
                <Camera className="w-4 h-4" /> Ambil / pilih foto<input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="return-photo-input" />
              </label>
            )}
          </div>
          <Button type="submit" disabled={busy || !form.item_name} className="w-full bg-rose-600 hover:bg-rose-700 text-white" data-testid="return-submit">{busy ? "Mengirim..." : "Ajukan Retur"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AssetReturnList({ refreshKey, onChanged }) {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const [rows, setRows] = useState(null);
  const load = () => api.get("/asset-returns").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line
  const terima = async (id) => {
    try { await api.post(`/asset-returns/${id}/terima`); toast.success("Retur dikonfirmasi diterima"); load(); onChanged?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows || !rows.length) return null;
  return (
    <Card className="bg-card border-border" data-testid="asset-return-list">
      <CardHeader className="pb-2"><CardTitle className="text-base font-heading flex items-center gap-2"><PackageX className="w-4 h-4 text-rose-500" /> Retur Alat Rusak <span className="text-xs font-normal text-muted-foreground">({rows.length})</span></CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="rounded-lg border border-border px-3 py-2 flex items-start justify-between gap-2 text-xs" data-testid={`return-row-${r.id}`}>
            {r.photo_path && <ZoomImg src={`${API_BASE}/asset-returns/files/${r.photo_path}`} alt="foto alat rusak" className="w-14 h-14 rounded-md object-cover border border-border shrink-0 cursor-zoom-in" data-testid={`return-photo-${r.id}`} />}
            <div className="min-w-0 space-y-0.5 flex-1">
              <div className="font-semibold text-sm">{r.item_name} <span className="font-mono text-rose-500">× {r.qty}</span> {r.unit_price ? <span className="text-muted-foreground font-normal">({rupiah(r.unit_price * r.qty)})</span> : null}</div>
              <div className="text-muted-foreground">{r.reason}</div>
              <div className="text-[10px] text-muted-foreground">{r.kt_name} · {fmtDateTime(r.created_at)}</div>
            </div>
            <div className="text-right shrink-0 space-y-1">
              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${r.status === "diterima_admin" ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" : "bg-amber-500/15 text-amber-500 border-amber-500/30"}`} data-testid={`return-status-${r.id}`}>{r.status === "diterima_admin" ? `Diterima admin (${r.received_by})` : "Menunggu admin"}</span>
              {isAdmin && r.status === "diajukan" && <div><Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => terima(r.id)} data-testid={`return-terima-${r.id}`}>Barang Diterima</Button></div>}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
